/**
 * AfeRepo sobre Supabase (backend compartido del piloto).
 * Misma lógica de dominio que DexieAfeRepo; el almacenamiento va a Postgres.
 */

import type { AfeRepo } from "../../repos/AfeRepo";
import type { Afe } from "@app-types/entities";
import { puedeTransicionar, TRANSICIONES_AFE } from "@constants/estados";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseAfeRepo extends SupabaseBaseRepo<Afe> implements AfeRepo {
  constructor(tenantId: string) {
    super("afe", tenantId);
  }

  async list(filter?: Partial<Afe>): Promise<Afe[]> {
    const all = await this.listAll();
    let res = all;
    if (filter?.estado) res = res.filter((a) => a.estado === filter.estado);
    if (filter?.budgetId) res = res.filter((a) => a.budgetId === filter.budgetId);
    if (filter?.tipo) res = res.filter((a) => a.tipo === filter.tipo);
    return res.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async get(id: string): Promise<Afe | null> {
    return this.getById(id);
  }

  async create(data: Omit<Afe, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const newAfe: Afe = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(newAfe);
    return newAfe.id;
  }

  async update(id: string, data: Partial<Afe>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`AFE ${id} not found`);
    const updated: Afe = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: Afe["estado"], _userId: string): Promise<void> {
    const afe = await this.get(id);
    if (!afe) throw new Error(`AFE ${id} not found`);
    if (!puedeTransicionar(afe.estado, nextState, TRANSICIONES_AFE)) {
      throw new Error(`Transicion ${afe.estado} -> ${nextState} no permitida`);
    }
    await this.update(id, { estado: nextState }, afe.version);
  }

  async getSaldoDisponible(id: string): Promise<number> {
    const afe = await this.get(id);
    if (!afe) return 0;
    return afe.valorAprobadoUsd;
  }
}
